const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const propertiesService = {
  // Get a single property by ID with all related data
  getProperty: async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching property:", error);
      throw error;
    }
  },

  // Get all properties
  getAllProperties: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching properties:", error);
      throw error;
    }
  },

  // Search properties
  searchProperties: async (query) => {
    try {
      const response = await fetch(`${API_BASE_URL}/properties/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ query }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error searching properties:", error);
      throw error;
    }
  },

  // Get property locations
  getPropertyLocations: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/locations`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching property locations:", error);
      throw error;
    }
  },

  // Get property types
  getPropertyTypes: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/types`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching property types:", error);
      throw error;
    }
  },

  // Get property liabilities
  getPropertyLiabilities: async (propertyId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/propertyliabilities?property_id=${propertyId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching property liabilities:", error);
      throw error;
    }
  },

  // Get number of active properties
  getNumberOfActiveProperties: async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/of-status/active/number`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching number of active properties:", error);
      throw error;
    }
  },

  // Export property details
  exportPropertyDetails: async (propertyId) => {
    try {
      // Get property data
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const property = data.data?.property;

      if (!property) {
        throw new Error("Property not found");
      }

      // Create CSV content
      const csvContent = [
        "Property ID,Name,Address,Postcode,Status,Lease Start,Lease End,Client",
        `${property.property_id || property.id},"${property.name}","${
          property.address_1
        }","${property.postcode}","${property.status_text}","${
          property.lease_start_date || "N/A"
        }","${property.lease_end_date || "N/A"}","${
          property.client?.name || "N/A"
        }"`,
      ].join("\n");

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `property-${propertyId}-details.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting property details:", error);
      throw error;
    }
  },

  exportPlacedGuardiansCSV: async (propertyId) => {
    try {
      // Get property data first
      const propertyResponse = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!propertyResponse.ok) {
        throw new Error(`HTTP error! status: ${propertyResponse.status}`);
      }

      // const propertyData = await propertyResponse.json(); // Unused variable removed
      // const property = propertyData.data?.property; // Unused variable removed

      // Get placed guardians data
      const guardiansResponse = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/guardians`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      let guardians = [];
      if (guardiansResponse.ok) {
        const guardiansData = await guardiansResponse.json();
        guardians = guardiansData.data?.guardians || [];
      }

      // Filter for placed guardians only
      const placedGuardians = guardians.filter(
        (guardian) =>
          guardian.status && guardian.status.toLowerCase().includes("placed")
      );

      // Create CSV content
      const headers = ["UGD", "Name", "Email", "Mobile", "Status", "Room"];
      const csvContent = [
        headers.join(","),
        ...placedGuardians.map((guardian) =>
          [
            guardian.unique_guardian_number || "N/A",
            `"${guardian.fullName || "N/A"}"`,
            `"${guardian.user?.email || "N/A"}"`,
            `"${guardian.user?.mobile_number || "N/A"}"`,
            `"${guardian.status || "N/A"}"`,
            `"${guardian.room?.name || "N/A"}"`,
          ].join(",")
        ),
      ].join("\n");

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `property-${propertyId}-placed-guardians.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting placed guardians CSV:", error);
      throw error;
    }
  },

  exportPlacedGuardiansPDF: async (propertyId) => {
    try {
      // Import jsPDF and autoTable dynamically
      const { default: jsPDF } = await import("jspdf");
      const autoTable = (await import("jspdf-autotable")).default;

      // Get property data first
      const propertyResponse = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!propertyResponse.ok) {
        throw new Error(`HTTP error! status: ${propertyResponse.status}`);
      }

      const propertyData = await propertyResponse.json();
      const property = propertyData.data?.property;

      // Get placed guardians data
      const guardiansResponse = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/guardians`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      let guardians = [];
      if (guardiansResponse.ok) {
        const guardiansData = await guardiansResponse.json();
        guardians = guardiansData.data?.guardians || [];
      }

      // Filter for placed guardians only
      const placedGuardians = guardians.filter(
        (guardian) =>
          guardian.status && guardian.status.toLowerCase().includes("placed")
      );

      // Create PDF
      const doc = new jsPDF();

      // Add title
      doc.setFontSize(16);
      doc.text(`Property ${propertyId} - Placed Guardians Report`, 20, 20);

      // Add property details
      doc.setFontSize(12);
      doc.text(`Property: ${property?.name || "N/A"}`, 20, 35);
      doc.text(`Address: ${property?.address_1 || "N/A"}`, 20, 45);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 55);

      // Prepare table data
      const tableData = placedGuardians.map((guardian) => [
        guardian.unique_guardian_number || "N/A",
        guardian.fullName || "N/A",
        guardian.user?.email || "N/A",
        guardian.user?.mobile_number || "N/A",
        guardian.status || "N/A",
        guardian.room?.name || "N/A",
      ]);

      // Add table
      autoTable(doc, {
        head: [["UGD", "Name", "Email", "Mobile", "Status", "Room"]],
        body: tableData,
        startY: 70,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [66, 139, 202] },
      });

      // Save PDF
      doc.save(`property-${propertyId}-placed-guardians.pdf`);
    } catch (error) {
      console.error("Error exporting placed guardians PDF:", error);
      throw error;
    }
  },

  // Export guardians details
  exportGuardiansDetails: async (propertyId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/guardians/export`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `property-${propertyId}-guardians.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting guardians details:", error);
      throw error;
    }
  },

  // Export guardians PDF
  exportGuardiansPDF: async (propertyId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/guardians/export-pdf`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `property-${propertyId}-guardians.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting guardians PDF:", error);
      throw error;
    }
  },

  // Create property document
  getPropertyDocumentTypes: async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/document-types`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching property document types:", error);
      throw error;
    }
  },

  createPropertyDocument: async (propertyId, documentData) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/document`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(documentData), // JSON data with S3 key
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
        message: "Document created successfully",
      };
    } catch (error) {
      console.error("Error creating property document:", error);
      return {
        success: false,
        message: error.message || "Error creating document",
      };
    }
  },

  // Get property documents
  getPropertyDocuments: async (
    propertyId,
    page = 1,
    filter = "all",
    search = ""
  ) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        filter: filter,
        search: search,
      });

      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/documents?${params}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data.documents || data,
        pagination: data.pagination,
        total: data.total,
      };
    } catch (error) {
      console.error("Error fetching property documents:", error);
      return {
        success: false,
        message: error.message || "Error fetching documents",
      };
    }
  },

  // Get property certificates
  getPropertyCertificates: async (propertyId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/certificates`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data.certificates || data,
      };
    } catch (error) {
      console.error("Error fetching property certificates:", error);
      return {
        success: false,
        message: error.message || "Error fetching certificates",
      };
    }
  },

  // Get process documents
  getProcessDocuments: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/documents`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data.process_documents || data,
      };
    } catch (error) {
      console.error("Error fetching process documents:", error);
      return {
        success: false,
        message: error.message || "Error fetching process documents",
      };
    }
  },

  // Delete property document
  deletePropertyDocument: async (propertyId, documentId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/documents/${documentId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      return {
        success: true,
        message: "Document deleted successfully",
      };
    } catch (error) {
      console.error("Error deleting property document:", error);
      return {
        success: false,
        message: error.message || "Error deleting document",
      };
    }
  },

  // Show property document (view details)
  showPropertyDocument: async (propertyId, documentId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/documents/${documentId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
        message: "Document details retrieved successfully",
      };
    } catch (error) {
      console.error("Error showing property document:", error);
      return {
        success: false,
        message: error.message || "Error showing document",
      };
    }
  },

  // Get property document for preview modal
  getPropertyDocument: async (propertyId, documentId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/documents/${documentId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data.data, // Extract the actual document data from the nested structure
        source: data.source,
        message: data.message || "Document data retrieved successfully",
      };
    } catch (error) {
      console.error("Error getting property document:", error);
      return {
        success: false,
        message: error.message || "Error getting document",
      };
    }
  },

  // Edit property document (get document for editing)
  editPropertyDocument: async (propertyId, documentId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/documents/${documentId}/edit`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: data.success,
        data: data.data,
        message: data.message || "Document edit data retrieved successfully",
      };
    } catch (error) {
      console.error("Error getting document edit data:", error);
      return {
        success: false,
        message: error.message || "Error getting document edit data",
      };
    }
  },

  // Update property document
  updatePropertyDocument: async (propertyId, documentId, formData) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/documents/${documentId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        // Extract error message from backend response
        const errorMessage = data.message || `HTTP error! status: ${response.status}`;
        return {
          success: false,
          message: errorMessage,
        };
      }

      return {
        success: true,
        data: data,
        message: "Document updated successfully",
      };
    } catch (error) {
      console.error("Error updating document:", error);
      return {
        success: false,
        message: error.message || "Error updating document",
      };
    }
  },

  // Download property document
  // ✅ FIXED: Backend now redirects to presigned URL, so this function follows the redirect
  downloadPropertyDocument: async (propertyId, documentId) => { // propertyId kept for API compatibility
    try {
      // Download the file using the documents download endpoint
      // Laravel equivalent: route('documents.download', $document->id)
      // Backend may return either:
      // 1. Binary file stream (PDF, images, etc.) - Content-Type: application/pdf, etc.
      // 2. JSON with presigned URL - Content-Type: application/json
      const response = await fetch(
        `${API_BASE_URL}/api/documents/${documentId}/download`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check Content-Type to determine if response is binary or JSON
      const contentType = response.headers.get("content-type") || "";
      
      if (contentType.includes("application/json")) {
        // JSON response with presigned URL
        const result = await response.json();

        if (!result.success || !result.data?.presignedUrl) {
          throw new Error(result.message || "Failed to get presigned URL");
        }

        // Use presigned URL directly for download (matching Laravel's direct S3 access)
        const presignedUrl = result.data.presignedUrl;
        const filename = result.data.documentName || `document-${documentId}.pdf`;

        console.log(`✅ Using presigned URL for direct S3 download: ${filename}`);

        // Create download link with presigned URL
        const a = document.createElement("a");
        a.href = presignedUrl;
        a.download = filename;
        a.target = "_blank"; // Open in new tab for direct S3 access
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        // Binary response (PDF, image, etc.) - handle as blob
        const blob = await response.blob();
        
        // Extract filename from Content-Disposition header or use default
        const contentDisposition = response.headers.get("content-disposition") || "";
        let filename = `document-${documentId}.pdf`;
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, "");
            // Decode URI-encoded filename
            try {
              filename = decodeURIComponent(filename);
            } catch (e) {
              // If decoding fails, use as-is
            }
          }
        }

        console.log(`✅ Downloading binary file: ${filename} (${blob.size} bytes)`);

        // Create blob URL and download link
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        // Clean up blob URL after a short delay
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      }

      return {
        success: true,
        message: "Document downloaded successfully",
      };
    } catch (error) {
      console.error("Error downloading property document:", error);
      return {
        success: false,
        message: error.message || "Error downloading document",
      };
    }
  },

  // Download document using direct URL (for media documents like bills/reports)
  // ✅ FIXED: Now handles both JSON (presigned URL) and binary (direct stream) responses
  downloadDocumentByUrl: async (downloadUrl, documentName) => {
    try {
      console.log(`📥 Downloading document from URL: ${downloadUrl}`);

      // Get presigned URL from backend (for media documents)
      const response = await fetch(
        `${API_BASE_URL}${downloadUrl}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check Content-Type to determine if response is binary or JSON
      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        // JSON response with presigned URL
        const result = await response.json();

        if (!result.success || !result.data?.presignedUrl) {
          throw new Error(result.message || "Failed to get presigned URL");
        }

        // Use presigned URL directly for download (matching Laravel's direct S3 access)
        const presignedUrl = result.data.presignedUrl;
        const filename = result.data.documentName || documentName || 'document.pdf';

        console.log(`✅ Using presigned URL for direct S3 download: ${filename}`);

        // Create download link with presigned URL
        const a = document.createElement("a");
        a.href = presignedUrl;
        a.download = filename;
        a.target = "_blank"; // Open in new tab for direct S3 access
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        // Binary response - download directly as blob
        const blob = await response.blob();

        // Try to get filename from Content-Disposition header, or use provided name
        const contentDisposition = response.headers.get("content-disposition") || "";
        let filename = documentName || "document.pdf";

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        console.log(`✅ Downloading binary file: ${filename} (${blob.size} bytes)`);

        // Create blob URL and download link
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Clean up blob URL after a short delay
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      }

      return {
        success: true,
        message: "Document downloaded successfully",
      };
    } catch (error) {
      console.error("Error downloading document by URL:", error);
      return {
        success: false,
        message: error.message || "Error downloading document",
      };
    }
  },

  // Store property certificate
  storeCertificate: async (propertyId, certificateData) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/certificate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(certificateData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
        message: "Certificate added successfully",
      };
    } catch (error) {
      console.error("Error adding certificate:", error);
      return {
        success: false,
        message: error.message || "Error adding certificate",
      };
    }
  },

  // Archive a property document
  archivePropertyDocument: async (propertyId, documentId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/documents/${documentId}/archive`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error archiving document:", error);
      throw error;
    }
  },

  // Activate a property document
  activatePropertyDocument: async (propertyId, documentId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/documents/${documentId}/activate`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error activating document:", error);
      throw error;
    }
  },

  // Get archived property documents
  getArchivedPropertyDocuments: async (propertyId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/documents/archived`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error("Error fetching archived documents:", error);
      throw error;
    }
  },
};

export default propertiesService;
